import { type JSX, ReactNode } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CheckboxComponent from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import {
  CheckboxesProps,
  ValuesGrid,
  useAttributeInlineLabel,
  useAttributeLayout,
  useVisibleAttributeValues,
} from "@plansoft-configuration-renderer/components";
import {
  useCsAttribute,
  useCsValue,
  useCsValueApi,
  useLocalOptionSelected,
} from "@plansoft-configuration-renderer/framework";

export function CustomCheckboxes(props: CheckboxesProps): JSX.Element {
  const values = useVisibleAttributeValues();
  const { formLabel } = useAttributeInlineLabel();
  const { fullWidth, inlineValues } = useAttributeLayout();

  const sortedValues = values.slice().sort((a, b) => {
    if (a.isSelected === b.isSelected) return 0;
    return a.isSelected ? -1 : 1;
  });

  return (
    <FormControl component="fieldset" fullWidth={fullWidth}>
      {formLabel}
      <FormGroup row={inlineValues}>
        <ValuesGrid
          values={sortedValues}
          ValueDisplayComponent={CheckboxOption}
        >
          {props.children}
        </ValuesGrid>
      </FormGroup>
    </FormControl>
  );
}

type CheckboxOptionProps = {
  children: ReactNode;
};

function CheckboxOption(props: CheckboxOptionProps): JSX.Element {
  const value = useCsValue();
  const attribute = useCsAttribute();
  const { toggleOption } = useCsValueApi();
  const isSelected = useLocalOptionSelected();

  const checkboxId = `checkbox-${attribute.id}-${value.value}`;

  const isDisabled = Boolean(
    !attribute.isAllowed ||
    attribute.isReadOnly ||
    !value.isAllowed ||
    !value.isActive,
  );

  return (
    <FormControlLabel
      sx={{ cursor: "default", ml: 0 }}
      id={checkboxId}
      checked={isSelected}
      disabled={false}
      onChange={(): Promise<void> => toggleOption(!isSelected)}
      control={
        <CheckboxComponent
          icon={<CloseIcon sx={{ color: "#B1192A" }} />}
          checkedIcon={<CheckIcon sx={{ color: "#14909A" }} />}
          disabled={isDisabled}
          onClick={(e): void => e.preventDefault()}
          sx={{ cursor: "default", p: 0.5 }}
        />
      }
      label={<>{props.children}</>}
      disableTypography={false}
    />
  );
}
